import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { buildDistributionItems } from '../helpers/distribution-items'
import { readLiveSelection } from '../helpers/live-selection'
import { commitShapeBatch } from '../handlers/commit-shape-batch'
import type { SpaceDirection } from '../types/layout-operations'
import type { OnBatchShapesCommitted } from '../types/layout-panel-props'

const SELECTION_GEOMETRY_UPDATED_EVENT = 'selection-geometry-updated'

function exitInlineTextEditing() {
  const active = document.activeElement

  if (active instanceof HTMLElement) {
    active.blur()
  }
}

function notifySelectionGeometryChanged() {
  window.dispatchEvent(new Event(SELECTION_GEOMETRY_UPDATED_EVENT))
}

type UseSpaceDistributionArgs = {
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
  onBatchShapesCommitted: OnBatchShapesCommitted | undefined
  selectedCount: number
  canSpaceSelection: boolean
  canAutoDistribute: boolean
}

/** Space Between direction + Auto spacing: commits auto/manual distribution of the live selection. */
export function useSpaceDistribution({
  shapeManagerRef,
  onBatchShapesCommitted,
  selectedCount,
  canSpaceSelection,
  canAutoDistribute,
}: UseSpaceDistributionArgs) {
  const [selectedSpaceBetween, setSelectedSpaceBetween] = useState<SpaceDirection | null>(null)
  const [autoDistribute, setAutoDistribute] = useState(false)

  const commitDistribution = useCallback(
    (mode: 'auto' | 'manual', direction: SpaceDirection, gap?: number) => {
      const mgr = shapeManagerRef?.current
      const items = buildDistributionItems(
        readLiveSelection(mgr),
        (id) => mgr?.getShapeBounds?.(id),
        mode,
        direction,
        gap
      )

      if (items.length === 0 || !mgr || !onBatchShapesCommitted) {
        return
      }

      commitShapeBatch(mgr, items, onBatchShapesCommitted, { kind: 'position' })
      notifySelectionGeometryChanged()
    },
    [onBatchShapesCommitted, shapeManagerRef]
  )

  const distributeSelectionAutomatically = useCallback(
    (direction: SpaceDirection) => {
      exitInlineTextEditing()
      commitDistribution('auto', direction)
    },
    [commitDistribution]
  )

  const applyManualSpacing = useCallback(
    (direction: SpaceDirection, gap: number) => {
      exitInlineTextEditing()
      commitDistribution('manual', direction, gap)
    },
    [commitDistribution]
  )

  useEffect(() => {
    if (selectedCount < 3) {
      setAutoDistribute(false)
      return
    }

    if (!autoDistribute || selectedSpaceBetween == null) {
      return
    }

    distributeSelectionAutomatically(selectedSpaceBetween)
  }, [autoDistribute, distributeSelectionAutomatically, selectedCount, selectedSpaceBetween])

  const handleSpaceDirectionClick = useCallback(
    (direction: SpaceDirection, gap: number) => {
      exitInlineTextEditing()
      setSelectedSpaceBetween(direction)

      if (autoDistribute) {
        distributeSelectionAutomatically(direction)
      } else if (canSpaceSelection) {
        applyManualSpacing(direction, gap)
      }
    },
    [applyManualSpacing, autoDistribute, canSpaceSelection, distributeSelectionAutomatically]
  )

  const handleAutoDistributeChange = useCallback(
    (checked: boolean) => {
      if (checked && !canAutoDistribute) {
        return
      }

      exitInlineTextEditing()
      setAutoDistribute(checked)

      if (checked && selectedSpaceBetween != null) {
        distributeSelectionAutomatically(selectedSpaceBetween)
      }
    },
    [canAutoDistribute, distributeSelectionAutomatically, selectedSpaceBetween]
  )

  return { autoDistribute, handleSpaceDirectionClick, handleAutoDistributeChange }
}
